/**
 * DebugPanel — always-on observability for CipherOps.
 *
 * Toggled via Ctrl+Shift+D (or Cmd+Shift+D on macOS) or ?debug=1 in
 * the URL. Renders six tabs (Session, State, API, Events, Errors,
 * Performance) over the bottom half of the viewport so the underlying
 * UI stays usable.
 *
 * Shippable to production: in production builds the panel is hidden
 * by default but the keyboard shortcut still works. Self-host
 * customers who hit a bug open the panel, click Export, and send us
 * the JSON bundle. We reconstruct the failure state without remote
 * access to their data.
 *
 * Mount this component once at the app root (in router.tsx for the
 * web SaaS, in MainLayout for the electron path). It's a no-op until
 * the user opens it.
 *
 * Per latest/ENGINEERING-PRACTICES.md.
 */

import React, { useEffect, useState, useSyncExternalStore } from 'react';
import {
  getDebugStore,
  subscribeDebugStore,
  downloadBundle,
  debugLog,
  type ApiCallEntry,
  type DebugEvent,
  type ErrorEntry,
  type PerformanceEntry,
} from '../lib/debug';

type Tab = 'session' | 'state' | 'api' | 'events' | 'errors' | 'performance';

// ============================================================================
// Hook — keyboard shortcut + ?debug=1 URL param
// ============================================================================

function useDebugPanelToggle(): [boolean, () => void] {
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('debug') === '1';
  });

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Ctrl+Shift+D (Win/Linux) or Cmd+Shift+D (macOS)
      const modifier = e.ctrlKey || e.metaKey;
      if (modifier && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
        e.preventDefault();
        setOpen((o) => {
          const next = !o;
          debugLog.recordEvent({
            type: 'debug_panel_toggled',
            description: next ? 'Debug panel opened' : 'Debug panel closed',
          });
          return next;
        });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return [open, () => setOpen((o) => !o)];
}

// ============================================================================
// External store subscription
// ============================================================================

function useDebugSnapshot() {
  return useSyncExternalStore(
    subscribeDebugStore,
    () => getDebugStore(),
    () => getDebugStore(),
  );
}

// ============================================================================
// Component
// ============================================================================

export default function DebugPanel(): React.ReactElement | null {
  const [open, toggle] = useDebugPanelToggle();
  const [activeTab, setActiveTab] = useState<Tab>('session');
  const snapshot = useDebugSnapshot();

  if (!open) {
    // 2026-07-06 refresh — the floating DEBUG pill is a dev-build affordance
    // only. In production it was a polish leak (visible bottom-right on every
    // screen); the documented support path there is Ctrl+Shift+D / ?debug=1,
    // both of which still work — only the always-on chip is gated.
    if (!import.meta.env.DEV) return null;
    // Render a tiny pill at the bottom-right corner so users discover
    // the panel exists. Click also toggles it.
    return (
      <button
        type="button"
        onClick={toggle}
        data-debug-id="debug-panel-fab"
        title="Open Debug Panel (Ctrl+Shift+D)"
        style={{
          position: 'fixed',
          right: 12,
          bottom: 12,
          zIndex: 9998,
          background: 'rgba(26,31,46,0.85)',
          color: '#d97a8a',
          border: '0.5px solid rgba(217,122,138,0.4)',
          padding: '4px 10px',
          fontFamily: 'var(--mono, "Courier New", monospace)',
          fontSize: 9,
          letterSpacing: 1.5,
          cursor: 'pointer',
          borderRadius: 2,
        }}
      >
        DEBUG
      </button>
    );
  }

  const tabs: Array<{ id: Tab; label: string; count: number }> = [
    { id: 'session', label: 'Session', count: Object.keys(snapshot.session).length },
    { id: 'state', label: 'State', count: Object.keys(snapshot.stateRegistry).length },
    { id: 'api', label: 'API', count: snapshot.apiCalls.length },
    { id: 'events', label: 'Events', count: snapshot.events.length },
    { id: 'errors', label: 'Errors', count: snapshot.errors.length },
    { id: 'performance', label: 'Perf', count: snapshot.performance.length },
  ];

  return (
    <div
      data-debug-id="debug-panel"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        height: '45vh',
        background: '#0d1118',
        color: '#cfd5e1',
        fontFamily: 'var(--mono, "Courier New", monospace)',
        fontSize: 11,
        lineHeight: 1.55,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        borderTop: '2px solid #8b1a2e',
        boxShadow: '0 -12px 40px rgba(0,0,0,0.5)',
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        padding: '6px 12px',
        background: '#1a1f2e',
        borderBottom: '0.5px solid #2a2f3e',
      }}>
        <span style={{ color: '#d97a8a', letterSpacing: 2, fontSize: 10, fontWeight: 600 }}>
          CIPHEROPS · DEBUG PANEL
        </span>
        <span style={{ marginLeft: 12, color: '#6b7280', fontSize: 9 }}>
          Ctrl+Shift+D to toggle
        </span>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => downloadBundle()}
          data-debug-id="debug-panel-export"
          style={{
            background: 'transparent',
            color: '#8ab4f8',
            border: '0.5px solid #2a4c7e',
            padding: '3px 9px',
            fontFamily: 'inherit',
            fontSize: 9,
            letterSpacing: 1,
            cursor: 'pointer',
            marginRight: 8,
          }}
        >
          EXPORT BUNDLE
        </button>
        <button
          type="button"
          onClick={toggle}
          data-debug-id="debug-panel-close"
          style={{
            background: 'transparent',
            color: '#cfd5e1',
            border: '0.5px solid #2a2f3e',
            padding: '3px 9px',
            fontFamily: 'inherit',
            fontSize: 9,
            cursor: 'pointer',
          }}
        >
          CLOSE ×
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        background: '#1a1f2e',
        borderBottom: '0.5px solid #2a2f3e',
      }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            data-debug-id={`debug-tab-${t.id}`}
            onClick={() => setActiveTab(t.id)}
            style={{
              background: activeTab === t.id ? '#0d1118' : 'transparent',
              color: activeTab === t.id ? '#d97a8a' : '#cfd5e1',
              border: 'none',
              borderBottom: activeTab === t.id ? '2px solid #d97a8a' : '2px solid transparent',
              padding: '8px 16px',
              fontFamily: 'inherit',
              fontSize: 10,
              letterSpacing: 1.5,
              cursor: 'pointer',
              textTransform: 'uppercase',
            }}
          >
            {t.label}
            {t.count > 0 && (
              <span style={{
                marginLeft: 6,
                padding: '0 5px',
                background: activeTab === t.id ? '#8b1a2e' : '#2a2f3e',
                color: '#fff',
                borderRadius: 1,
                fontSize: 9,
              }}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '10px 14px' }}>
        {activeTab === 'session' && <SessionTab data={snapshot.session as Record<string, unknown>} />}
        {activeTab === 'state' && <StateTab registry={snapshot.stateRegistry} />}
        {activeTab === 'api' && <ApiTab calls={snapshot.apiCalls} />}
        {activeTab === 'events' && <EventsTab events={snapshot.events} />}
        {activeTab === 'errors' && <ErrorsTab errors={snapshot.errors} />}
        {activeTab === 'performance' && <PerformanceTab entries={snapshot.performance} />}
      </div>
    </div>
  );
}

// ============================================================================
// Tab components
// ============================================================================

function SessionTab({ data }: { data: Record<string, unknown> }): React.ReactElement {
  const entries = Object.entries(data);
  if (entries.length === 0) {
    return (
      <div style={{ color: '#6b7280' }}>
        No session info registered yet. Call <code style={{ color: '#8ab4f8' }}>setSession(...)</code> from your auth bootstrap.
      </div>
    );
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        {entries.map(([k, v]) => (
          <tr key={k}>
            <td style={{ padding: '3px 8px', color: '#8ab4f8', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{k}</td>
            <td style={{ padding: '3px 8px', color: '#c5b783', wordBreak: 'break-word' }}>
              {v === null || v === undefined ? <span style={{ color: '#6b7280' }}>—</span> : String(v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StateTab({ registry }: { registry: Record<string, unknown> }): React.ReactElement {
  const entries = Object.entries(registry);
  if (entries.length === 0) {
    return (
      <div style={{ color: '#6b7280' }}>
        No screens have registered state. Use <code style={{ color: '#8ab4f8' }}>useDebugRegister('ScreenName', state)</code> in your component.
      </div>
    );
  }
  return (
    <>
      {entries.map(([name, state]) => (
        <div key={name} style={{ marginBottom: 16 }}>
          <div style={{ color: '#d97a8a', fontWeight: 600, marginBottom: 4, letterSpacing: 1, fontSize: 10 }}>
            {name}
          </div>
          <pre style={{
            margin: 0,
            padding: '8px 10px',
            background: '#1a1f2e',
            color: '#c5b783',
            fontSize: 11,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            borderRadius: 2,
          }}>
            {JSON.stringify(state, null, 2)}
          </pre>
        </div>
      ))}
    </>
  );
}

function ApiTab({ calls }: { calls: ApiCallEntry[] }): React.ReactElement {
  if (calls.length === 0) {
    return (
      <div style={{ color: '#6b7280' }}>
        No API calls recorded yet. The data-source layer's <code style={{ color: '#8ab4f8' }}>invoke()</code> wraps every call and lands them here.
      </div>
    );
  }
  return (
    <>
      {[...calls].reverse().map((c, i) => (
        <ApiCallRow key={`${c.startedAt}-${i}`} call={c} />
      ))}
    </>
  );
}

function ApiCallRow({ call }: { call: ApiCallEntry }): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{
      marginBottom: 6,
      padding: '6px 10px',
      background: call.ok ? '#152021' : '#2a1818',
      borderLeft: `2px solid ${call.ok ? '#2d7a42' : '#c44a1a'}`,
      cursor: 'pointer',
    }} onClick={() => setExpanded((e) => !e)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ color: call.ok ? '#2d7a42' : '#c44a1a', fontWeight: 600 }}>
          {call.ok ? '✓' : '✗'} {call.status ?? (call.ok ? '200' : 'ERR')}
        </span>
        <span style={{ color: '#8ab4f8' }}>{call.channel}</span>
        <span style={{ color: '#6b7280' }}>{call.durationMs.toFixed(0)}ms</span>
        {call.requestId && (
          <span style={{ color: '#6b7280', fontSize: 9 }}>req={call.requestId.slice(0, 8)}</span>
        )}
        <span style={{ marginLeft: 'auto', color: '#6b7280', fontSize: 9 }}>{call.startedAt.slice(11, 19)}</span>
      </div>
      {expanded && (
        <div style={{ marginTop: 6, paddingTop: 6, borderTop: '0.5px solid #2a2f3e' }}>
          <div style={{ color: '#8ab4f8', fontSize: 9, marginBottom: 2 }}>REQUEST</div>
          <pre style={{ margin: 0, color: '#c5b783', fontSize: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {JSON.stringify(call.payload ?? null, null, 2)}
          </pre>
          <div style={{ color: '#8ab4f8', fontSize: 9, marginTop: 8, marginBottom: 2 }}>
            {call.ok ? 'RESPONSE' : 'ERROR'}
          </div>
          <pre style={{ margin: 0, color: call.ok ? '#c5b783' : '#d97a8a', fontSize: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {JSON.stringify(call.ok ? call.response : call.error, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function EventsTab({ events }: { events: DebugEvent[] }): React.ReactElement {
  if (events.length === 0) {
    return (
      <div style={{ color: '#6b7280' }}>
        No events recorded yet. Use <code style={{ color: '#8ab4f8' }}>debugLog.recordEvent(...)</code>.
      </div>
    );
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        {[...events].reverse().map((e, i) => (
          <tr key={`${e.at}-${i}`} style={{ borderBottom: '0.5px solid #1a1f2e' }}>
            <td style={{ padding: '4px 8px', color: '#6b7280', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
              {e.at.slice(11, 19)}
            </td>
            <td style={{ padding: '4px 8px', color: '#d97a8a', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
              {e.type}
            </td>
            <td style={{ padding: '4px 8px', color: '#c5b783', verticalAlign: 'top' }}>
              {e.description}
              {e.data && (
                <pre style={{ margin: '4px 0 0', fontSize: 10, color: '#cfd5e1', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(e.data, null, 2)}
                </pre>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ErrorsTab({ errors }: { errors: ErrorEntry[] }): React.ReactElement {
  if (errors.length === 0) {
    return (
      <div style={{ color: '#2d7a42' }}>
        No errors captured this session. ErrorBoundary + API failures land here automatically.
      </div>
    );
  }
  return (
    <>
      {[...errors].reverse().map((e, i) => (
        <div key={`${e.at}-${i}`} style={{
          marginBottom: 10,
          padding: '8px 10px',
          background: '#2a1818',
          borderLeft: '2px solid #c44a1a',
        }}>
          <div style={{ color: '#c44a1a', fontWeight: 600, marginBottom: 4 }}>
            {e.name}: {e.message}
          </div>
          <div style={{ color: '#6b7280', fontSize: 9, marginBottom: 6 }}>{e.at}</div>
          {e.stack && (
            <details>
              <summary style={{ color: '#8ab4f8', cursor: 'pointer', fontSize: 9 }}>Stack trace</summary>
              <pre style={{ margin: '4px 0 0', fontSize: 10, color: '#cfd5e1', whiteSpace: 'pre-wrap' }}>
                {e.stack}
              </pre>
            </details>
          )}
          {e.componentStack && (
            <details>
              <summary style={{ color: '#8ab4f8', cursor: 'pointer', fontSize: 9 }}>Component stack</summary>
              <pre style={{ margin: '4px 0 0', fontSize: 10, color: '#cfd5e1', whiteSpace: 'pre-wrap' }}>
                {e.componentStack}
              </pre>
            </details>
          )}
          {e.stateSnapshot && Object.keys(e.stateSnapshot).length > 0 && (
            <details>
              <summary style={{ color: '#8ab4f8', cursor: 'pointer', fontSize: 9 }}>State at error</summary>
              <pre style={{ margin: '4px 0 0', fontSize: 10, color: '#c5b783', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(e.stateSnapshot, null, 2)}
              </pre>
            </details>
          )}
        </div>
      ))}
    </>
  );
}

function PerformanceTab({ entries }: { entries: PerformanceEntry[] }): React.ReactElement {
  if (entries.length === 0) {
    return (
      <div style={{ color: '#6b7280' }}>
        No performance marks. Use <code style={{ color: '#8ab4f8' }}>debugLog.recordPerformance(...)</code> or wrap with <code style={{ color: '#8ab4f8' }}>performance.measure()</code>.
      </div>
    );
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ borderBottom: '0.5px solid #2a2f3e' }}>
          <th style={{ padding: '4px 8px', textAlign: 'left', color: '#6b7280', fontSize: 9, letterSpacing: 1 }}>TIME</th>
          <th style={{ padding: '4px 8px', textAlign: 'left', color: '#6b7280', fontSize: 9, letterSpacing: 1 }}>NAME</th>
          <th style={{ padding: '4px 8px', textAlign: 'right', color: '#6b7280', fontSize: 9, letterSpacing: 1 }}>DURATION</th>
        </tr>
      </thead>
      <tbody>
        {[...entries].reverse().map((p, i) => (
          <tr key={`${p.at}-${i}`}>
            <td style={{ padding: '3px 8px', color: '#6b7280' }}>{p.at.slice(11, 19)}</td>
            <td style={{ padding: '3px 8px', color: '#8ab4f8' }}>
              {p.category && <span style={{ color: '#6b7280', marginRight: 6 }}>{p.category}</span>}
              {p.name}
            </td>
            <td style={{ padding: '3px 8px', color: p.durationMs > 1000 ? '#c44a1a' : p.durationMs > 100 ? '#b8860b' : '#2d7a42', textAlign: 'right' }}>
              {p.durationMs.toFixed(1)}ms
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
